// ListPage.tsx
import React, { forwardRef, ReactNode, useImperativeHandle, useState, useEffect } from 'react';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';

import CreateButton from './CreateButton';
import PageHeader from '../layout/PageHeader';
import ListPageFilterBar from './ListPageFilterBar';
import InteractiveDataTable from './InteractiveDataTable';
import LoadingWrapper from '../layout/LoadingWrapper';

import { FilterValues } from '../form/model';
import { AppliedFilter } from '../form/model';
import axios from '../utils/axios.utils';

interface ListPageProps {
  afterComponent?: ReactNode;
  beforeComponent?: ReactNode;
  backButtonLabel?: string;
  backButtonLink?: string;
  clearedFilterSetting?: any;
  clickableRows?: boolean;
  columns: any;
  controller: string;
  createLink?: string;
  defaultSort?: string;
  defaultFilters?: any;
  expandableRowsComponent?: any;
  filterDefinitions?: any;
  includeFilterBar?: boolean;
  perPage?: number;
  queryPlaceholder?: string;
  selectableRows?: boolean;
  title: string;
  subtitle?: string;
  additionalParams?: Record<string, any>;
  onRowClick?: (row: any) => void;
  rowClickCondition?: (row: any) => boolean;
  rowClickPath?: string;
  rowClickQueryString?: string;
  button?: ReactNode;
}

export interface ListPageRef {
  refetch: () => void;
}

const ListPage = forwardRef<ListPageRef, ListPageProps>((props, ref) => {
  const {
    afterComponent,
    backButtonLabel,
    backButtonLink,
    beforeComponent,
    clearedFilterSetting,
    clickableRows,
    columns,
    controller,
    createLink,
    defaultFilters,
    defaultSort = 'name asc',
    expandableRowsComponent,
    filterDefinitions,
    includeFilterBar,
    perPage,
    queryPlaceholder = 'Search',
    selectableRows,
    subtitle,
    title,
    additionalParams = {},
    onRowClick,
    rowClickCondition,
    rowClickPath,
    rowClickQueryString,
    button,
  } = props;

  const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>([]);
  const [downloadedList, setDownloadedList] = useState<Record<string, string>[]>([]);
  const [filterValues, setFilterValues] = useState<FilterValues>(defaultFilters);
  const [loading, setLoading] = useState<boolean>(false);
  const [records, setRecords] = useState<Record<string, string>[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortParam, setSortParam] = useState<string>(defaultSort);
  const [totalRecords, setTotalRecords] = useState<number>();

  function toRansackParams(params: FilterValues) {
    const parts: string[] = [];
    for (const key in params) {
      const value: any = (params as any)[key];
      if (Array.isArray(value)) {
        value.forEach((v) => parts.push(`ransack[${key}][]=${encodeURIComponent(v)}`));
      } else if (value !== undefined && value !== null && value !== '') {
        parts.push(`ransack[${key}]=${encodeURIComponent(value)}`);
      }
    }
    return parts.join('&');
  }

  const fetchRecordsList = async (
    controllerName: string,
    searchFilters: FilterValues,
    inputPage: number,
    query?: string,
    sort?: string,
  ): Promise<void> => {
    try {
      setLoading(true);

      const searchParams = toRansackParams(searchFilters);
      let urlWithParams = `${controllerName}?${searchParams}&page=${inputPage}`;
      if (query) urlWithParams += `&query=${encodeURIComponent(query)}`;
      if (sort) urlWithParams += `&sort=${encodeURIComponent(sort)}`;

      // Add any additional parameters
      Object.keys(additionalParams).forEach((key) => {
        if (additionalParams[key] !== undefined && additionalParams[key] !== null) {
          urlWithParams += `&${key}=${encodeURIComponent(additionalParams[key])}`;
        }
      });

      const response = await axios.get<string, any>(urlWithParams);
      setRecords(response?.data?.result || []);
      setDownloadedList([]);
      setTotalRecords(response?.data?.total_records);
      setAppliedFilters(response?.data?.applied_filters || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecordsList(controller, defaultFilters, 1, '', defaultSort);
  }, [controller]);

  useImperativeHandle(
    ref,
    () => ({
      refetch: () => fetchRecordsList(controller, filterValues, 1, searchQuery, sortParam),
    }),
    [controller, filterValues, searchQuery, sortParam],
  );

  const afterClear = () => {
    setSortParam('');
    setFilterValues(clearedFilterSetting);
    setSearchQuery('');
    fetchRecordsList(controller, clearedFilterSetting, 1, '', '');
  };

  return (
    <Grid container style={{ padding: '1%' }}>
      <Grid item xs={12} md={8}>
        <PageHeader
          mainTitle={title}
          subTitle={subtitle}
          backButtonLabel={backButtonLabel}
          backButtonLink={backButtonLink}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          alignItems: 'flex-end',
        }}
      >
        {button ?? <CreateButton createLink={createLink} />}
      </Grid>
      <Grid item xs={12} style={{ marginBottom: 10 }}>
        {beforeComponent}
      </Grid>
      <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}></Grid>
      {includeFilterBar && (
        <Grid item xs={12}>
          <ListPageFilterBar
            afterClear={afterClear}
            filterDefinitions={filterDefinitions}
            filterValues={filterValues}
            setFilterValues={setFilterValues}
            controller={controller}
            fetchRecordsList={fetchRecordsList}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            queryPlaceholder={queryPlaceholder}
          />
        </Grid>
      )}
      <Grid item xs={12} style={{ marginTop: 20 }}>
        <Card>
          <InteractiveDataTable
            clickableRows={clickableRows}
            controller={controller}
            columns={columns}
            loading={loading}
            expandableRowsComponent={expandableRowsComponent}
            fetchRecordsList={fetchRecordsList}
            filterValues={filterValues}
            perPage={perPage}
            records={records}
            searchQuery={searchQuery}
            selectableRows={selectableRows}
            sortParam={sortParam}
            setSortParam={setSortParam}
            totalRecords={totalRecords}
            onRowClick={onRowClick}
            rowClickCondition={rowClickCondition}
            rowClickPath={rowClickPath}
            rowClickQueryString={rowClickQueryString}
          />
        </Card>
      </Grid>

      <Grid item xs={12} style={{ marginBottom: 10 }}>
        {afterComponent}
      </Grid>
    </Grid>
  );
});

ListPage.displayName = 'ListPage';

export default ListPage;
